import { useEffect, useState } from "react";
import { TypedUseSelectorHook, useSelector } from "react-redux";
import Wallet from "../components/Wallet";
import { supabase } from "../supabase/supabaseClient";
import { useMatch } from "react-router-dom";
import { GraphIcon } from "../components/Icons";
import Loader from "../components/Loader";
import { RootState } from "../redux/store";
import {
  CommissionType,
  DepositType,
  TradeType,
  WithdrawType,
} from "../models/Transactions";
import { paginationModel } from "../components/TransactionColumns";
import DataTable from "../components/Datatable";
import TradeTerminal from "../components/Terminal";
import { EdgeType, NodeType } from "../models/Hierarchy.model";
import dagre from "dagre";
import Hierarchy from "../components/Hierarchy";

const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

function Home() {
  const user = useTypedSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(true);
  const [trades, setTrades] = useState<
    CommissionType[] | TradeType[] | DepositType[] | WithdrawType[]
  >([]);

  const [nodes, setNodes] = useState<NodeType[]>([]);
  const [edges, setEdges] = useState<EdgeType[]>([]);

  const homeMatch = useMatch("/home");
  const commissionMatch = useMatch("/commission");
  const withdrawMatch = useMatch("/withdraw");
  const depositMatch = useMatch("/deposit");
  const hierarchyMatch = useMatch("/hierarchy");

  const getCommission = async () => {
    setIsLoading(true);
    // change to 'to' (currently set to from just for displaying data)
    const { data } = await supabase
      .from("Commission")
      .select("*")
      .eq("to", user.id)
      .order("created_at", { ascending: false });

    if (data) {
      const reorderedData: CommissionType[] = data.map((d, i) => {
        const temp = {
          id: i + 1,
          from: d?.from,
          amount: d?.amount.toFixed(4),
          level: d?.level,
          isPaid: d?.isPaid,
          createdAt: d?.created_at,
        };
        return temp;
      });
      setTrades(reorderedData);
    }
    setIsLoading(false);
    return null;
  };

  const getTrades = async () => {
    setIsLoading(true);

    const { data } = await supabase
      .from("Transaction")
      .select("*")
      .eq("telegram_id", user.id)
      .lte("created_at", new Date(Date.now() - 3 * 60 * 1000).toISOString()) // Exclude entries less than 3 minutes ago
      .order("created_at", { ascending: false });

    if (data) {
      const reorderedData: TradeType[] = data.map((d, i) => {
        const temp = {
          id: i + 1,
          profitUsd: d?.profit?.usd.toFixed(2),
          profitPol: d?.profit?.matic.toFixed(2),
          oldBalance: d?.balance?.old.toFixed(2),
          newBalance: d?.balance?.new.toFixed(2),
          oldCredit: d?.credit?.old.toFixed(2),
          newCredit: d?.credit?.new.toFixed(2),
          createdAt: d?.created_at,
        };
        return temp;
      });
      setTrades(reorderedData);
    }
    setIsLoading(false);
    return null;
  };

  const getDeposit = async () => {
    setIsLoading(true);

    const { data } = await supabase
      .from("Deposits")
      .select("*")
      .eq("telegram_id", user.id)
      .order("created_at", { ascending: false });

    if (data) {
      const reorderedData: DepositType[] = data.map(
        (d: DepositType, i: number) => {
          const temp = {
            id: i + 1,
            address_bsc: d?.address_bsc,
            address_trc: d?.address_trc,
            network: d?.network,
            currency: d?.currency,
            requested_amount: d?.requested_amount,
            actual_amount: d?.actual_amount,
            hash: d?.hash,
            status: d?.status,
            remark: d?.remark,
            created_at: d?.created_at,
          };
          return temp;
        }
      );
      setTrades(reorderedData);
    }
    setIsLoading(false);

    return null;
  };

  const getWithdraws = async () => {
    setIsLoading(true);

    const { data } = await supabase
      .from("Withdraws")
      .select("*")
      .eq("telegram_id", user.id)
      .order("created_at", { ascending: false });

    if (data) {
      const reorderedData: WithdrawType[] = data.map((d, i) => {
        const temp = {
          id: i + 1,
          address: d?.address,
          network: d?.network,
          currency: d?.currency,
          amount: d?.amount.toFixed(2),
          hash: d?.hash,
          status: d?.status,
          remark: d?.remark,
          createdAt: d?.created_at,
        };
        return temp;
      });
      setTrades(reorderedData);
    }
    setIsLoading(false);

    return null;
  };

  // Run bfs to construct tree structure
  const getHierarchy = async () => {
    setIsLoading(true);
    const telegramId = user.id.toString();

    const rootNode = {
      id: telegramId,
      type: "customNode",
      position: { x: 0, y: 0 },
      data: { label: telegramId, leaderPayout: 0 },
    };

    const fetchedNodes = [rootNode];
    const fetchedEdges = [];

    const queue = [{ id: telegramId, x: 0, y: 0, level: 0 }];

    while (queue.length > 0) {
      // Process one level at a time
      const currentLevel = queue[0].level;
      const currentLevelNodes = queue.filter(
        (item) => item.level === currentLevel
      );

      // Remove nodes at this level from the queue to process the next level
      currentLevelNodes.forEach((node) => {
        const index = queue.indexOf(node);
        if (index > -1) queue.splice(index, 1);
      });

      // Collect all children for this entire level
      let childrenToPlace: { parentId: string; childId: string[] }[] = [];
      for (const parentNode of currentLevelNodes) {
        const children = await getChildren(parentNode.id!);
        if (children.length > 0) {
          childrenToPlace.push({
            parentId: parentNode.id!,
            childId: children,
          });
        }
      }

      // Skip if no children at this level
      if (childrenToPlace.length === 0) {
        continue;
      }

      const nextLevel = currentLevel + 1;
      const newNodes: { id: string; x: number; y: number; parentId: string }[] =
        [];

      for (const { parentId, childId } of childrenToPlace) {
        childId.forEach((cId) => {
          newNodes.push({ id: cId, x: 0, y: 0, parentId });
        });
      }

      // Update nodes and edges
      for (const node of newNodes) {
        fetchedNodes.push({
          id: node.id,
          type: "customNode",
          position: {
            x: 0,
            y: 0,
          },
          data: { label: node.id, leaderPayout: 0 },
        });
        fetchedEdges.push({
          id: `e${node.parentId}-${node.id}`,
          source: node.parentId,
          target: node.id,
        });

        // Add to queue for next level
        queue.push({ id: node.id, x: 0, y: 0, level: nextLevel });
      }
    }

    // Position the nodes
    runDagreLayout(fetchedNodes as NodeType[], fetchedEdges);

    setIsLoading(false);
  };

  const runDagreLayout = (nodes: NodeType[], edges: EdgeType[]) => {
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: "TB", ranksep: 200, nodesep: 100 });
    g.setDefaultEdgeLabel(() => ({}));

    // Set every node width and height
    nodes.forEach((node) => {
      g.setNode(node.id, { width: 180, height: 100 });
    });

    edges.forEach((edge) => {
      g.setEdge(edge.source, edge.target);
    });

    // Initialize layout
    dagre.layout(g);

    // Now map back positions
    const newNodes = nodes.map((node) => {
      const coords = g.node(node.id);
      return {
        ...node,
        position: {
          x: coords.x,
          y: coords.y,
        },
      };
    });

    setNodes(newNodes);
    setEdges(edges);
  };

  const getChildren = async (telegramId: string) => {
    const { data: userData, error } = await supabase
      .from("Users")
      .select("child")
      .eq("telegram_id", telegramId)
      .single();

    if (error) {
      // toast.error(`Error getting child information with telegram ID: ${telegramId}`);
      return [];
    }
    return userData?.child || [];
  };

  useEffect(() => {
    if (homeMatch) {
      getTrades();
    }
    if (commissionMatch) {
      getCommission();
    }
    if (depositMatch) {
      getDeposit();
    }
    if (withdrawMatch) {
      getWithdraws();
    }
    if (hierarchyMatch) {
      getHierarchy();
    }
  }, [homeMatch, commissionMatch, depositMatch, withdrawMatch]);

  const dataGridProps = {
    initialState: { pagination: { paginationModel } },
    pageSizeOptions: [5, 10],
    sx: { border: 0 },
  };

  return (
    <div className="w-screen h-full flex items-center justify-center flex-col z-[-1000]">
      <div className="w-full">
        <Wallet />
        {homeMatch && (
          <div className="w-full p-10">
            <TradeTerminal telegramId={user?.id} />
          </div>
        )}

        <div className="p-10">
          {isLoading ? (
            <Loader isLoading={isLoading} />
          ) : (
            <>
              <DataTable trades={trades} dataGridProps={dataGridProps} />
              {hierarchyMatch && (
                <>
                  <div className="text-2xl text-red font-semibold mb-5 flex gap-2 items-center">
                    <span>Hierarchy</span>
                    <GraphIcon />
                  </div>
                  <div className="p-5 rounded-lg bg-pureWhite shadow-md">
                    <Hierarchy
                      nodes={nodes}
                      edges={edges}
                      setNodes={setNodes}
                      setEdges={setEdges}
                      isLoading={isLoading}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;
